import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Globe, Zap, Shield, TrendingUp, Play, ArrowRight } from "lucide-react";

const StudyWithStrangers = () => {
  const features = [
    {
      icon: <Globe className="w-8 h-8" />,
      title: "Global Community",
      description: "Join thousands of students studying around the world",
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Instant Join",
      description: "No invites needed - jump into any public room instantly",
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Safe Environment",
      description: "Moderated spaces designed for respectful, focused studying",
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Stay Motivated",
      description: "See others working hard and feel inspired to do your best",
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO 
        title="Study With Strangers | CSW | Online Workspace | Productivity Tool | Study Stream"
        description="Join public study rooms with students worldwide. Instant access, safe environment, and global community. Stay motivated studying with strangers."
        keywords="study with strangers, public study room, global study community, study online, virtual study room, study with others"
        canonicalUrl="/study-with-strangers/"
      />
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
          {/* Animated Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-background via-muted to-background" />
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
          
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="max-w-5xl mx-auto text-center space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 animate-fade-in">
                <Globe className="w-4 h-4 text-primary" />
                <span className="text-sm font-semibold text-primary">GLOBAL STUDY COMMUNITY</span>
              </div>
              
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight animate-fade-in" style={{ animationDelay: '0.1s' }}>
                <span className="text-gradient">Study With Strangers</span>
              </h1>
              
              <p className="text-xl sm:text-2xl text-muted-foreground max-w-3xl mx-auto font-light animate-fade-in" style={{ animationDelay: '0.2s' }}>
                Experience the power of collective focus. Join public study rooms and feel the energy of a global study community.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4 animate-fade-in" style={{ animationDelay: '0.3s' }}>
                <Button 
                  size="lg"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                  asChild
                >
                  <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                    Join Public Room <ArrowRight className="ml-2 w-5 h-5" />
                  </a>
                </Button>
              </div>
              
              {/* Video Section */}
              <div className="relative mt-12 animate-fade-in" style={{ animationDelay: '0.4s' }}>
                <div className="relative rounded-2xl overflow-hidden border-2 border-border/50 bg-card/30 backdrop-blur-sm shadow-2xl">
                  <video 
                    autoPlay 
                    loop 
                    muted 
                    playsInline
                    className="w-full aspect-video object-cover"
                  >
                    <source src="/videos/productivity-demo.mp4" type="video/mp4" />
                  </video>
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                  <div className="absolute bottom-6 left-6 flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-primary/90 flex items-center justify-center glow-primary">
                      <Play className="w-5 h-5 text-primary-foreground ml-1" />
                    </div>
                    <span className="text-foreground font-medium">See how it works</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="font-display text-4xl sm:text-5xl font-bold mb-4">
                  Why Study With <span className="text-gradient">Others?</span>
                </h2>
                <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                  Discover the benefits of studying in a global community
                </p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {features.map((feature, index) => (
                  <Card 
                    key={index} 
                    className="text-center bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 animate-fade-in"
                    style={{ animationDelay: `${0.1 * index}s` }}
                  >
                    <CardHeader>
                      <div className="mx-auto mb-4 w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center text-primary">
                        {feature.icon}
                      </div>
                      <CardTitle className="text-xl">{feature.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-base">{feature.description}</CardDescription>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute top-1/2 right-0 w-72 h-72 bg-primary/10 rounded-full blur-[100px]" />
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-center mb-16">
                The Science of <span className="text-gradient">Social Studying</span>
              </h2>
              <div className="space-y-8">
                {[
                  { title: "Social Accountability", desc: "When you see others working, you're naturally motivated to stay focused. Public study rooms create positive peer pressure that keeps you on track." },
                  { title: "Combat Loneliness", desc: "Studying doesn't have to be isolating. Feel connected to a community of learners, even if you never say a word." },
                  { title: "Discover New Perspectives", desc: "Meet students from different backgrounds, studying different subjects, working towards their own goals. Their dedication inspires yours." },
                ].map((item, index) => (
                  <Card key={index} className="p-8 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/30 transition-all animate-fade-in" style={{ animationDelay: `${0.1 * index}s` }}>
                    <h3 className="text-2xl font-semibold mb-4 text-foreground">{item.title}</h3>
                    <p className="text-muted-foreground text-lg">{item.desc}</p>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Room Types */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-center mb-16">
                Choose Your <span className="text-gradient">Vibe</span>
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                {[
                  { title: "Chill Mode", subtitle: "Low-pressure environment for light work", desc: "Perfect for reading, brainstorming, or creative work that doesn't need intense focus." },
                  { title: "Study Mode", subtitle: "Focused atmosphere for serious work", desc: "Ideal for assignments, exam prep, and deep learning. Everyone's here to work." },
                  { title: "Work Mode", subtitle: "High-intensity productivity zone", desc: "For professionals and students tackling challenging projects with tight deadlines." },
                ].map((item, index) => (
                  <Card key={index} className="p-8 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105">
                    <h3 className="text-2xl font-bold text-foreground mb-2">{item.title}</h3>
                    <p className="text-primary font-medium mb-4">{item.subtitle}</p>
                    <p className="text-muted-foreground">{item.desc}</p>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px]" />
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
                Join the Global <span className="text-gradient">Study Movement</span>
              </h2>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Thousands of students are studying right now. Be part of the community.
              </p>
              <Button 
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-12 py-6 text-lg glow-primary transition-all hover:scale-105"
                asChild
              >
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Start Studying Now
                </a>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default StudyWithStrangers;